var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * Kebab dropdown menu rendered in the right-side `lastMenu` slot of the
 * authority / proposal list pane header. Hosts the actions that used to live
 * as bare buttons (Export), the file Import (CZNA-1106) and the type-scoped
 * "Revalidate" action.
 *
 * The menu is intentionally generic — each consumer (AuthoritiesList,
 * ProposalsList) passes the actions it wants via the `actions` prop, so a
 * list view can opt in to just Export without dragging the validation
 * machinery along.
 */
import React, { useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Dropdown, DropdownMenu, Icon, PaneMenu, } from '@folio/stripes/components';
import dropdownTriggerFocus from '../../utils/dropdownTriggerFocus';
/**
 * CZNA-537 — inline note under a disabled action. Inline rather than a stylesheet to
 * match the rest of this module, which carries no CSS files.
 */
/**
 * CZNA-537 — the greyed-out look, applied by hand.
 *
 * Every dimming rule in stripes-components keys on the `disabled` ATTRIBUTE or the
 * `:disabled` pseudo-class (interactionStyles `opacity: .65`, Button.css `pointer-events`),
 * and there is no `[aria-disabled]` selector anywhere in the library. Switching the item
 * to aria-disabled so its description stays reachable therefore also switched off its
 * appearance — leaving an item that looks and hovers like a live control and does nothing.
 * That is the ticket's own complaint pointed the other way, so the styling comes back here.
 */
var INERT_ITEM_STYLE = {
    opacity: 0.65,
    cursor: 'default',
};
var DISABLED_REASON_STYLE = {
    padding: '0 1rem 0.5rem 2.4rem',
    maxWidth: '28rem',
    fontSize: '0.85em',
    lineHeight: 1.35,
    color: '#5b5b5b',
};
var noop = function () { };
var ListActionsMenu = function (_a) {
    var actions = _a.actions, activeJob = _a.activeJob, _b = _a.id, id = _b === void 0 ? 'list-actions-menu' : _b;
    var intl = useIntl();
    var _c = useState(false), open = _c[0], setOpen = _c[1];
    var handleToggle = useCallback(function () { return setOpen(function (prev) { return !prev; }); }, []);
    var closeAndRun = useCallback(function (run) { return function () {
        setOpen(false);
        run();
    }; }, []);
    if (actions.length === 0)
        return null;
    var renderItem = function (action) {
        switch (action.key) {
            case 'export': {
                var reason = action.disabled ? action.disabledReason : undefined;
                // Per format: two export items render at once, and a shared id would point both
                // aria-describedby attributes at whichever div rendered last.
                var reasonId = "".concat(id, "-export-").concat(action.format.toLowerCase(), "-disabled-reason");
                var isMarc = action.format === 'MARCXML';
                return (React.createElement(React.Fragment, { key: "".concat(action.key, "-").concat(action.format) },
                    React.createElement(Button, { buttonStyle: "dropdownItem", role: "menuitem", 
                        // aria-disabled, not `disabled`, when there is a reason to read: a real
                        // `disabled` button cannot take focus, so it is skipped in menu navigation
                        // and its aria-describedby never fires — the explanation would exist only
                        // for a sighted user who hovers. aria-disabled keeps the item focusable and
                        // announced as unavailable, and the guard in the handler below is what
                        // actually stops the action. Falls back to `disabled` when there is no
                        // reason (the transient isExporting case), where nothing is lost.
                        disabled: action.disabled && !reason, "aria-disabled": action.disabled || undefined, onClick: action.disabled ? noop : closeAndRun(action.onClick), style: reason ? INERT_ITEM_STYLE : undefined, title: reason, "aria-describedby": reason ? reasonId : undefined, "data-test-list-action": "export-".concat(action.format.toLowerCase()) },
                        React.createElement(Icon, { icon: "download" }, action.count === null ? (React.createElement(FormattedMessage, { id: isMarc
                                ? 'ui-czna-authorities.authorities.export.buttonMarcUnknownCount'
                                : 'ui-czna-authorities.authorities.export.buttonUnknownCount' })) : (React.createElement(FormattedMessage, { id: isMarc
                                ? 'ui-czna-authorities.authorities.export.buttonMarc'
                                : 'ui-czna-authorities.authorities.export.button', values: { count: action.count } })))),
                    reason && (React.createElement("div", { id: reasonId, role: "none", style: DISABLED_REASON_STYLE, "data-test-list-action-disabled-reason": "export-".concat(action.format.toLowerCase()) }, reason))));
            }
            case 'import':
                return (React.createElement(Button, { key: action.key, buttonStyle: "dropdownItem", role: "menuitem", disabled: action.disabled, onClick: closeAndRun(action.onClick), "data-test-list-action": "import" },
                    React.createElement(Icon, { icon: "arrow-up" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.importFile.menuItem" }))));
            case 'revalidate-type': {
                var disabledByJob = !!activeJob;
                return (React.createElement(Button, { key: action.key, buttonStyle: "dropdownItem", role: "menuitem", disabled: action.disabled || disabledByJob, onClick: closeAndRun(action.onClick), "data-test-list-action": "revalidate-type" },
                    React.createElement(Icon, { icon: "refresh" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.listActions.revalidateType", values: { type: action.recordTypeLabel } }))));
            }
            default:
                return null;
        }
    };
    return (React.createElement(PaneMenu, null,
        React.createElement(Dropdown, { id: id, open: open, onToggle: handleToggle, renderTrigger: function (_a) {
                var getTriggerProps = _a.getTriggerProps, triggerRef = _a.triggerRef, onToggle = _a.onToggle, ariaProps = _a.ariaProps, keyHandler = _a.keyHandler;
                return (React.createElement(Button, __assign({ ref: triggerRef, buttonStyle: "default", marginBottom0: true, onClick: onToggle, onKeyDown: keyHandler }, dropdownTriggerFocus(getTriggerProps), { "data-test-list-actions-trigger": "true" }, ariaProps),
                    React.createElement(Icon, { icon: "ellipsis" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.listActions.menuLabel" }))));
            } },
            React.createElement(DropdownMenu, { "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.listActions.menuLabel' }) },
                React.createElement("div", { role: "menu", "data-test-list-actions-menu": true }, actions.map(renderItem))))));
};
export default ListActionsMenu;
